import React from "react";
import styled from "styled-components";
import { SectionComponent } from "./Section/Section";
import {
  ScreenConfig,
  StageElement,
  type ThemeVariant,
  type ViewerDeviceRuntime,
  getDefaultViewerDeviceRuntime,
  GetAuthTokenCallback,
} from "../../../types";
import { DefaultComponentRenderer } from "../default";
import ImageElement from "../image";
import TextElement from "../TextElement";
import TableElementViewer from "../TableElement";
import { renderPluginByType } from "../../../util";
import { STAGE_ELEMENT } from "../../../constants";
import { createSectionsStyles, DEFAULT_STAGE_STYLES } from "../../../util/createSectionsStyles";
import { StageElementWrapper } from "./StageElement/StageElementWrapper";
import type { Media, Link } from "@bronte/viewer/src/data/ViewerConfig";
import { RendererTypes } from "@bronte/viewer/src/main";

const RceStage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  /* BV parity: BV's screen plugin (Aquila) puts box-sizing: border-box on its content
     root and inherits it into every descendant, so theme rules that combine an explicit
     width with padding/border (e.g. the chapter-opener hero figcaption: width 416px +
     40px padding + 3px border) size to the declared width. Express content defaults to
     content-box without this, rendering such boxes wider and re-wrapping their text. */
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }

  /* BV parity: iframe-big widens the plugin iframe past the column (theme sets
     920-1100px) and in BV the overhang is silently cut at the screen plugin's
     frame edge - no scrollbar. Express has no frame boundary, so without this the
     overhang reaches the page and grows a horizontal scrollbar. The stage spans
     the same width as BV's frame (the section does not: classes like example_2
     cap it at 940px, which would cut a 920px embed BV shows whole); clip (not
     hidden) avoids creating a scroll container. */
  &:has(> .iframe-big) {
    overflow-x: clip;
  }

  &.spf-rce-stage.spf-readonly {
    @media (max-width: 1024px) {
      overflow-x: auto;
    }
  }
`;

export interface StageComponentProps {
  env?: string; // TODO: can we be more specific? e.g. "dev"|"qa"|"prod" ?
  screenConfig: ScreenConfig;
  themeVariant?: Partial<ThemeVariant>;
  viewerDevice?: ViewerDeviceRuntime;
  onClickReferenceLink?: (dataId: string, referenceId: string) => void;
  onRequestGlossaryData?: () => Promise<any[]>;
  onRequestAuthToken?: GetAuthTokenCallback;
  onMediaClick?: (data: Media) => void;
  onMediaPlay?: (data: Media) => void;
  onLinkClick?: (data: Link) => void;
  renderer?: RendererTypes;
}

/**
 * StageComponent is the main component that renders the screenConfig.
 * @param env - The environment
 * @param screenConfig - The screen configuration
 * @param onClickReferenceLink -  when a reference link is clicked
 * @param onRequestGlossaryData -  when a glossary data is needed
 * @param onRequestAuthToken -  when an auth token is needed
 */
export const StageComponent: React.FC<StageComponentProps> = ({
  screenConfig,
  env,
  themeVariant,
  viewerDevice,
  onClickReferenceLink,
  onRequestGlossaryData,
  onRequestAuthToken,
  onMediaClick,
  onLinkClick,
  renderer,
}) => {
  const resolvedViewerDevice = viewerDevice ?? getDefaultViewerDeviceRuntime();
  const stageStyles = screenConfig.styles ?? DEFAULT_STAGE_STYLES;
  const elementsPadding = stageStyles?.elements?.padding ?? { vertical: 10, horizontal: 10 };
  const sectionCount = screenConfig.stage?.sections?.length ?? 0;

  const renderStageElement = (element: StageElement, elementIndex: string): JSX.Element | null => {
    if (!element.type) {
      return null;
    }

    if (element.type === STAGE_ELEMENT.PLACEHOLDER) {
      return null;
    }

    let elementComponent: JSX.Element | null = null;

    switch (element.type) {
      case STAGE_ELEMENT.IMAGE:
        elementComponent = (
          <ImageElement
            key={elementIndex}
            element={element}
            readOnly={true}
            onMediaClick={onMediaClick}
            viewerDevice={resolvedViewerDevice}
          />
        );
        break;
      case STAGE_ELEMENT.TEXT:
        console.log("TextElement", element);
        elementComponent = (
          <TextElement
            key={elementIndex}
            element={element}
            onClickReferenceLink={onClickReferenceLink}
            onMediaClick={onMediaClick}
            viewerDevice={resolvedViewerDevice}
          />
        );
        break;
      case STAGE_ELEMENT.TABLE:
        elementComponent = (
          <TableElementViewer
            key={elementIndex}
            element={element}
            onClickReferenceLink={onClickReferenceLink}
            onMediaClick={onMediaClick}
            viewerDevice={resolvedViewerDevice}
            themeVariant={themeVariant}
          />
        );
        break;
      case STAGE_ELEMENT.PLUGIN:
        elementComponent = renderPluginByType(
          element,
          elementIndex,
          screenConfig,
          onRequestGlossaryData,
          onRequestAuthToken,
          env,
          onMediaClick,
          themeVariant,
          resolvedViewerDevice,
          onLinkClick,
          renderer,
        );
        break;
      default:
        elementComponent = <DefaultComponentRenderer key={elementIndex} element={element} />;
    }

    if (!elementComponent) {
      return null;
    }
    console.log("elementComponent 343", elementComponent);

    return (
      <StageElementWrapper
        key={elementIndex}
        element={element}
        elementIndex={elementIndex}
        elementsPadding={elementsPadding}
      >
        {elementComponent}
      </StageElementWrapper>
    );
  };

  console.log("HERE is the screen config", screenConfig);
  if (!screenConfig.stage || !screenConfig.stage.sections) {
    return null;
  }
  // BV gates its narrow-screen section rules on this flag (StageContainer `responsive`)
  const responsiveClass = stageStyles.stage?.responsive ? " stage-responsive" : "";

  return (
    <RceStage className={`spf-rce-stage spf-readonly${responsiveClass}`}>
      {screenConfig.stage.sections.map((section, sectionPosition) => (
        <SectionComponent
          key={`section-${sectionPosition}`}
          section={section}
          renderStageElement={renderStageElement}
          defaultStyles={createSectionsStyles(stageStyles, sectionPosition, sectionCount, true)}
          viewerDevice={resolvedViewerDevice}
        />
      ))}
    </RceStage>
  );
};
